/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { Lang, useText } from '@adobe-commerce/elsie/i18n';
import { useEffect, useRef } from 'preact/compat';
import {
  ApplePay as ApplePayPaymentMethod,
  ApplePayRenderConfig,
} from '@adobe-commerce/payment-services-sdk/payment';
import { DEFAULT_LOCALE, useLocale } from '@/hooks/useLocale';
import * as signals from '@/lib/signals';
import LocalizedError, { fromPaymentsSDKError } from '@/lib/localizedError';
import { PaymentLocation } from '@/api';
import { useState } from 'preact/hooks';

export interface ApplePayProps {
  /**
   * Location where the Apple Pay button is to be rendered.
   */
  location: PaymentLocation;
  /**
   * Required if createCart not provided.
   * Should return a promise that resolves to the shopper's cart ID.
   */
  getCartId?: () => Promise<string>;
  /**
   * Required if getCartId not provided.
   */
  createCart?: {
    /**
     * Should return a list with the cart items to purchase.
     *
     * The list must contain at least one cart item.
     */
    getCartItems: () => CartItem[];
  };
  /**
   * Called when the user clicks the Apple Pay button. This callback receives a 'showPaymentSheet' function as its only
   * argument that must be called to begin the Apple Pay session and show the payment sheet.
   *
   * IMPORTANT: The 'showPaymentSheet' function must be called synchronously. If called asynchronously, it will throw
   *        ... an exception "Must create a newApplePaySession from a user gesture handler."
   */
  onButtonClick?: (showPaymentSheet: () => void) => void;
  /**
   * Called when payment flow is successful.
   */
  onSuccess?: (result: { cartId: string }) => void;
  /**
   * Called when the payment flow was aborted due to an error.
   *
   * The function receives an object with two properties, { name: string, message: string }, containing the localized
   * error name and message. Both properties are user-facing and can be translated using the
   * "PaymentServices.ApplePay.errors" language definitions.
   */
  onError?: (localizedError: LocalizedError) => void;
  /**
   * Whether the button is hidden. Set this to true to hide the Apple Pay button (default: false).
   */
  hidden?: boolean;
  /**
   * Whether the button is disabled. Set this to true to disable the Apple Pay button (default: false).
   */
  disabled?: boolean;
}

/**
 * Apple pay button container element class.
 */
const CONTAINER_CSS_CLASS: string = 'payment-services_apple-pay-container';

/**
 * Helper type for locale format that the Payments JS SDK works with for Apple Pay.
 */
type ApplePayLocale = 'en-US';

function getSDK(location: PaymentLocation) {
  const paymentServicesSDKs = signals.paymentsSDK.value;
  switch (location) {
    case PaymentLocation.CHECKOUT:
      return paymentServicesSDKs?.checkout;
    case PaymentLocation.PRODUCT_DETAIL:
      return paymentServicesSDKs?.productDetail;
    default:
      throw new Error(`Unsupported location: ${location}`);
  }
}

export const ApplePay = ({
  location,
  getCartId,
  createCart,
  onButtonClick,
  onSuccess,
  onError,
  hidden,
  disabled,
}: ApplePayProps) => {
  if ((!getCartId && !createCart) || (getCartId && createCart)) {
    throw new Error(
      'Either getCartId or createCart must be provided and not both.'
    );
  }

  const sdk = getSDK(location);
  const [applePay, setApplePay] = useState<ApplePayPaymentMethod | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const storeFrontLocale = useLocale();
  const initialDisabled = useRef<boolean>(!!disabled);

  const {
    methodNotAvailableMessage,
    methodNotLoadedMessage,
    methodLoadingMessage,
  } = useText({
    methodNotAvailableMessage: 'PaymentServices.messages.methodNotAvailable',
    methodNotLoadedMessage: 'PaymentServices.messages.methodNotLoaded',
    methodLoadingMessage: 'PaymentServices.messages.methodLoading',
  });

  useEffect(() => {
    let shadowCartIdPromise: Promise<{ cartId: string }> | null = null;
    if (!sdk?.Payment.ApplePay.isAvailable()) return;
    if (!containerRef.current) return;

    const containerId = `apple-pay-${Math.random().toString(36).slice(2, 11)}`;
    containerRef.current.id = containerId;

    const getOrCreateCart: () => Promise<string> = async () => {
      if (getCartId) {
        return getCartId();
      }
      const cartItems = createCart!.getCartItems(); // Can't be null nor undefined due to the check above.
      if (!cartItems) {
        throw new Error('No items found to be added to the cart.');
      }
      shadowCartIdPromise = sdk._Utils.addProductsToNewCart(
        toCartItemInputs(cartItems)
      );
      return (await shadowCartIdPromise).cartId;
    };

    const onCancel = () => {
      if (shadowCartIdPromise !== null) {
        shadowCartIdPromise
          .then(async ({ cartId }) => {
            await sdk._Utils.setCartAsInactive(cartId);
          })
          .catch(console.error);
      }
    };

    const renderConfig: ApplePayRenderConfig = {
      getCartId: getOrCreateCart,
      useApplePayShippingAddress: location === PaymentLocation.PRODUCT_DETAIL,
      useApplePayBillingAddress: location === PaymentLocation.PRODUCT_DETAIL,
      onSuccess,
      onError: (error) => {
        if (onError === undefined) {
          console.error(error);
        } else {
          onError(fromPaymentsSDKError('ApplePay', error));
        }
      },
      buttonLanguage: { locale: toAppleLocale(storeFrontLocale) },
      container: `#${containerId}`,
      onButtonClick,
      onCancel,
      disabled: initialDisabled.current,
    };

    let newApplePayInstance: ApplePayPaymentMethod | null = null;
    sdk.Payment.ApplePay.render(renderConfig)
      .then((instance) => {
        newApplePayInstance = instance;
        setApplePay(newApplePayInstance);
      })
      .catch((error) => {
        console.error('Apple Pay render failed:', error);
      });

    return () => {
      if (newApplePayInstance) {
        newApplePayInstance.teardown();
      }
    };
  }, [
    sdk,
    location,
    getCartId,
    createCart,
    onSuccess,
    onError,
    onButtonClick,
    storeFrontLocale,
  ]);

  useEffect(() => {
    if (applePay) {
      applePay.setDisabled(!!disabled);
    }
  }, [applePay, disabled]);

  const styles = {
    display: hidden ? 'none' : 'block',
  };

  if (sdk && !sdk.Payment.ApplePay.isAvailable()) {
    return (
      <div ref={containerRef} class={CONTAINER_CSS_CLASS} style={styles}>
        <div class="apple-pay-error">{methodNotAvailableMessage}</div>
      </div>
    );
  }

  if (signals.status.value === 'error') {
    return (
      <div ref={containerRef} class={CONTAINER_CSS_CLASS} style={styles}>
        <div class="apple-pay-error">{methodNotLoadedMessage}</div>
      </div>
    );
  }

  if (signals.status.value === 'initializing') {
    return (
      <div ref={containerRef} class={CONTAINER_CSS_CLASS} style={styles}>
        <div class="apple-pay-loading">{methodLoadingMessage}</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} class={CONTAINER_CSS_CLASS} style={styles}>
      {/* Apple Pay button will be rendered here by the SDK */}
    </div>
  );
};

function toCartItemInputs(cartItems: CartItem[]) {
  return cartItems.map((cartItem) => ({
    sku: cartItem.sku,
    quantity: cartItem.quantity,
    parent_sku: cartItem.parentSku,
    selected_options: cartItem.selectedOptions,
    entered_options: cartItem.enteredOptions,
  }));
}

/**
 * Maps elsie locale to the format that the Payment Services SDK expects for Apple Pay: a formatted string
 * consisting of an ISO-639-1 language code and an ISO-3166-1 region code, separated by a hyphen (-),
 * e.g., "nl-NL".
 */
function toAppleLocale(locale: Lang): ApplePayLocale {
  const parts = locale.split('_'); // e.g., split "en_US" into ["en", "US"]
  if (parts.length < 2) {
    const default_ = toAppleLocale(DEFAULT_LOCALE);
    console.warn(
      `Cannot map elsie locale "${locale}" to apple pay button locale. Falling back to "${default_}".`
    );
    return default_;
  }
  return `${parts[0]}-${parts[1]}` as ApplePayLocale;
}

/**
 * See https://developer.adobe.com/commerce/webapi/graphql-api/index.html#definition-CartItemInput.
 */
interface CartItem {
  sku: string;
  quantity: number;
  parentSku?: string;
  selectedOptions?: (string | number)[];
  enteredOptions?: { uid: string | number; value: string }[];
}
